import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import Tooltip from '@material-ui/core/Tooltip';

import CompetitorPositionBadge, { AKA, SHIRO } from '../badges/CompetitorPositionBadge';
import StatusBadge, { STATUSES } from '../badges/StatusBadge';
import NationalTeamBadge from '../badges/NationalTeamBadge';

/**
 * Competition Design System — the SINGLE representation of a competitor.
 *
 * In bracket contexts the AKA/SHIRO position badge is ALWAYS rendered above
 * the athlete name (never after it); tabular surfaces (results, shooting,
 * medalists) omit `position` and get name/club/rank alone. Club sits under
 * the name, then the rank. An empty slot
 * still shows its position badge + "(empty)" — never "BYE" as the primary
 * visual — with an optional Walkover / Auto-advance status badge. The winner
 * highlight and the position badge always coexist.
 *
 * This component owns competitor visualization for the WHOLE platform:
 * bracket, schedule, match view, live results, final results, shooting
 * results, scoreboard. Extend it here; never fork an athlete card per page.
 */
const SIZE = {
    sm: { name: 12, sub: 10, pad: 4, gap: 1 },
    md: { name: 15, sub: 12, pad: 8, gap: 3 },
};

const useStyles = makeStyles(theme => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: theme.palette.background.paper,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    headerTail: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
    },
    name: {
        fontWeight: 600,
        lineHeight: 1.15,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: theme.palette.text.primary,
    },
    sub: {
        lineHeight: 1.1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: theme.palette.text.secondary,
    },
    empty: {
        fontStyle: 'italic',
        color: theme.palette.text.disabled,
    },
    winnerName: {
        fontWeight: 700,
        color: theme.palette.success ? theme.palette.success.dark : '#2e7d32',
    },
    winnerCard: {
        background: 'rgba(76,175,80,0.10)',
    },
    loserCard: {
        background: 'rgba(0,0,0,0.03)',
    },
    loserName: {
        color: theme.palette.text.disabled,
    },
    modified: {
        fontSize: 10,
        lineHeight: 1,
    },
    // THE COUNTRY, on its own and in colour (21 Oct 2026) — so a secretary
    // drawing an international category sees at a glance who shares one.
    country: {
        display: 'inline-block',
        padding: '0 4px',
        borderRadius: 3,
        color: '#fff',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: 0.3,
    },
    // THE QUALIFICATION as a mark of its own (22 Oct 2026: «треба виділяти
    // якось розряд, щоб було гарно видно») — outlined, so it reads apart from
    // the solid country mark and the status badges.
    qualification: {
        display: 'inline-block',
        padding: '0 4px',
        borderRadius: 3,
        border: '1px solid #fb8c00',
        background: '#fff3e0',
        color: '#e65100',
        fontWeight: 700,
        lineHeight: 1.3,
    },
    number: {
        display: 'inline-block',
        marginRight: 4,
        padding: '0 3px',
        borderRadius: 2,
        background: 'rgba(0,0,0,0.08)',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 600,
    },
    annotation: {
        fontWeight: 700,
        color: theme.palette.text.secondary,
    },
}));

/** `2014-03-09` → `09.03.2014`, the way the competition papers write it. */
const formatDate = iso => {
    const [y, m, d] = String(iso).split('-');
    return d && m && y ? `${d}.${m}.${y}` : iso;
};

/**
 * WHAT HOVERING AN ATHLETE SHOWS (21 Oct 2026) — every line the athlete has,
 * none for what they lack. `hint` is the slot's own word (e.g. «click to
 * mark as winner»), kept because the card replaces the plain tooltip.
 */
const CompetitorDetails = ({ details, hint }) => {
    const [t] = useTranslation();
    const lines = [
        ['club', details.club],
        ['country', details.country],
        ['region', details.region],
        ['weight', details.weight ? t('competition.competitor.details.weightValue', { weight: details.weight, defaultValue: '{{weight}} kg' }) : null],
        ['rank', details.rank],
        ['qualification', details.qualification],
        ['trainer', details.trainer],
        ['age', null === details.age || undefined === details.age ? null : String(details.age)],
        ['birthday', details.birthday ? formatDate(details.birthday) : null],
    ].filter(([, value]) => value);

    return (
        <div data-testid='competitor-details'>
            {details.name && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{details.name}</div>}
            {lines.map(([key, value]) => (
                <div key={key} data-testid={`competitor-details-${key}`} style={{ fontSize: 12 }}>
                    <span style={{ opacity: 0.75 }}>{t(`competition.competitor.details.${key}`)}{': '}</span>
                    {value}
                </div>
            ))}
            {hint && <div style={{ fontSize: 11, marginTop: 4, opacity: 0.75, fontStyle: 'italic' }}>{hint}</div>}
        </div>
    );
};

const CompetitorCard = props => {
    const {
        position, name, number, club, country, countryColor, rank, qualification,
        empty, status, winner, annotation, modified, nationalTeam, size, className, children,
        details, detailsHint,
    } = props;
    const classes = useStyles();
    const [t] = useTranslation();
    const dims = SIZE[size] || SIZE.md;

    // A win is a first-class visual state even when no explicit status is set.
    const effectiveStatus = winner ? 'winner' : status;
    const isWinner = effectiveStatus === 'winner';
    const isLoser = effectiveStatus === 'loser';
    // Secondary chip shown for meaningful states (not the plain winner/loser
    // highlight, which is conveyed by the card tint + bold name).
    const badgeStatus = status && status !== 'winner' && status !== 'loser' ? status : null;

    const card = (
        <div
            className={[
                classes.card,
                isWinner ? classes.winnerCard : '',
                isLoser ? classes.loserCard : '',
                className,
            ].filter(Boolean).join(' ')}
            style={{ padding: dims.pad, gap: dims.gap }}
            data-testid='competitor-card'
            data-position={position || undefined}
            data-empty={empty ? 'true' : 'false'}
            data-status={effectiveStatus || 'none'}
            data-winner={isWinner ? 'true' : 'false'}
        >
            {(position || country || (qualification && !empty) || badgeStatus || nationalTeam || modified || annotation) && (
            <div className={classes.header}>
                {position && <CompetitorPositionBadge position={position} size={size === 'sm' ? 'sm' : 'md'} />}
                {country && (
                    <span
                        className={classes.country}
                        style={{ fontSize: dims.sub, background: countryColor || '#757575' }}
                        data-testid='competitor-country'
                        data-color={countryColor || ''}
                    >
                        {country}
                    </span>
                )}
                {/* In the top row, not a line of its own: a bracket card has no
                    height for a fourth line under the club. */}
                {qualification && !empty && (
                    <span className={classes.qualification} style={{ fontSize: dims.sub }} data-testid='competitor-qualification'>
                        {qualification}
                    </span>
                )}
                {badgeStatus && STATUSES[badgeStatus] && <StatusBadge status={badgeStatus} size='sm' />}
                {nationalTeam && <NationalTeamBadge size='sm' />}
                {(modified || annotation) && (
                    <span className={classes.headerTail}>
                        {modified && (
                            <span
                                role='img'
                                aria-label='modified'
                                className={classes.modified}
                                title={modified}
                                data-testid='slot-modified'
                            >
                                {'✏️'}
                            </span>
                        )}
                        {annotation && (
                            <span
                                className={classes.annotation}
                                style={{ fontSize: dims.sub }}
                                data-testid='slot-annotation'
                            >
                                {annotation}
                            </span>
                        )}
                    </span>
                )}
            </div>
            )}

            {empty ? (
                <div className={classes.empty} style={{ fontSize: dims.sub }} data-testid='competitor-empty'>
                    {t('competition.competitor.empty', '(empty)')}
                </div>
            ) : (
                <React.Fragment>
                    <div
                        className={[classes.name, isWinner ? classes.winnerName : '', isLoser ? classes.loserName : ''].filter(Boolean).join(' ')}
                        style={{ fontSize: dims.name }}
                        data-testid='competitor-name'
                    >
                        {/* COMPETITION NUMBER — the number the athlete wears,
                            before the name because that is how they are
                            called and looked up. Absent when the competition
                            does not use numbers, or has not assigned them
                            yet; the backend decides, this only renders. */}
                        {number ? (
                            <span className={classes.number} data-testid='competitor-number'>
                                {number}
                            </span>
                        ) : null}
                        {name || '—'}
                    </div>
                    {club && (
                        <div className={classes.sub} style={{ fontSize: dims.sub }} data-testid='competitor-club'>
                            {club}
                        </div>
                    )}
                    {rank && (
                        <div className={classes.sub} style={{ fontSize: dims.sub }} data-testid='competitor-meta'>
                            <span data-testid='competitor-rank'>{'🥋 '}{rank}</span>
                        </div>
                    )}
                </React.Fragment>
            )}
            {children}
        </div>
    );

    if (!details || empty) {
        return card;
    }

    return (
        <Tooltip
            title={<CompetitorDetails details={details} hint={detailsHint} />}
            placement='right-start'
            enterDelay={300}
            arrow
        >
            {card}
        </Tooltip>
    );
};

CompetitorCard.propTypes = {
    /** The athlete's competition number, or null when there is none. */
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** AKA/SHIRO for bracket slots; omit on tabular surfaces (results, shooting, medals). */
    position: PropTypes.oneOf([AKA, SHIRO]),
    name: PropTypes.string,
    club: PropTypes.string,
    /** The country's three-letter code (its name where none is known). */
    country: PropTypes.string,
    /** Everything about the athlete, shown on hover — see CompetitorDetails. */
    details: PropTypes.object,
    /** A last line under the details (the slot's own action). */
    detailsHint: PropTypes.string,
    /** Its colour in this category — see utils/Competition/countryColors. */
    countryColor: PropTypes.string,
    rank: PropTypes.string,
    qualification: PropTypes.string,
    empty: PropTypes.bool,
    status: PropTypes.oneOf(Object.keys(STATUSES)),
    winner: PropTypes.bool,
    annotation: PropTypes.node,
    modified: PropTypes.string,
    /** National Team classification — independent of `status` (never a CompetitionAttendanceStatus value). */
    nationalTeam: PropTypes.bool,
    size: PropTypes.oneOf(['sm', 'md']),
    className: PropTypes.string,
    children: PropTypes.node,
};

CompetitorCard.defaultProps = {
    position: null,
    name: null,
    club: null,
    country: null,
    countryColor: null,
    details: null,
    detailsHint: null,
    rank: null,
    qualification: null,
    empty: false,
    status: null,
    winner: false,
    annotation: null,
    modified: null,
    nationalTeam: false,
    size: 'md',
    className: null,
    children: null,
};

export default CompetitorCard;
